import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const Section = styled.section`
  padding: 100px 24px;
  background-color: white;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
`;

const FAQItem = styled.div`
  border-bottom: 1px solid #e2e8f0;
  &:last-child {
    border-bottom: none;
  }
`;

const Question = styled.button<{ isOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  
  span {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.isOpen ? '#002147' : '#334155'};
    transition: color 0.2s;
  }

  &:hover span {
    color: #002147;
  }
`;

const Answer = styled(motion.div)`
  overflow: hidden;
  color: #64748b;
  font-size: 16px;
  line-height: 1.6;
`;

const EducationFAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "Does SmushChat integrate with our LMS (Moodle, Canvas, Blackboard)?",
      answer: "Yes! We offer native integrations for Moodle, Canvas, and Blackboard. You can automatically enroll students, check course progress, and sync student data directly from the chat interface without any manual data entry."
    },
    {
      question: "Is student data secure and compliant (GDPR/FERPA)?",
      answer: "Absolutely. We take data privacy seriously. SmushChat is fully GDPR and FERPA compliant. All data is encrypted at rest and in transit, and we offer enterprise-grade security features for our University plans."
    },
    {
      question: "Can the chatbot handle multiple languages for international students?",
      answer: "Yes, SmushChat supports over 95 languages. The AI automatically detects the student's language and responds in their native tongue instantly, making it perfect for international recruitment."
    },
    {
      question: "How long does implementation take?",
      answer: "For most institutions, we can get you up and running in less than 48 hours. Our 'Campus' plan includes priority onboarding where we help you set up your knowledge base and integrations."
    },
    {
      question: "Can we customize the chatbot to match our university branding?",
      answer: "Yes, you can fully customize the chatbot's colors, logo, avatar, and tone of voice to match your institution's brand identity perfectly."
    }
  ];

  return (
    <Section id="faq">
      <Container>
        <Header>
          <Title>Frequently Asked Questions</Title>
          <Subtitle>Everything you need to know about automating your admissions.</Subtitle>
        </Header>

        <div>
          {faqs.map((faq, index) => (
            <FAQItem key={index}>
              <Question
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span>{faq.question}</span>
                {openIndex === index ? <Minus size={20} color="#002147" /> : <Plus size={20} color="#94a3b8" />}
              </Question>
              <AnimatePresence>
                {openIndex === index && (
                  <Answer
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div style={{ paddingBottom: '24px' }}>
                      {faq.answer}
                    </div>
                  </Answer>
                )}
              </AnimatePresence>
            </FAQItem>
          ))}
        </div>
      </Container>
    </Section>
  );
};

export default EducationFAQ;
